import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { CustomGroup } from '../../api/models/CustomGroup';
import { useCustomGroupUsers } from '../../hooks/useCustomGroupUsers';
import useCustomGroupUsersForm from '../../hooks/useCustomGroupUsersForm';
import ActionConfirmation from '../../../../Common/components/ActionConfirmation/ActionConfirmation';
import NavigationPrompt from '../../../../Common/components/NavigationPrompt/NavigationPrompt';
import CustomGroupUsersTable from '../CustomGroupUsersTable/CustomGroupUsersTable';
import { User } from '../../../../Common/models/FormModels';
import { removeItem } from '../../../../Common/utils/storageUtil';

interface EditCustomGroupUsersDialogProps {
  open: boolean;
  onClose: () => void;
  group: CustomGroup | null;
  projectId: string;
  toolId: string;
  toolProjectId: string;
}

const EditCustomGroupUsersDialog: React.FC<EditCustomGroupUsersDialogProps> = ({
  open,
  onClose,
  group,
  projectId,
  toolId,
  toolProjectId,
}) => {
  const { fetchUsers } = useCustomGroupUsers();
  const [initialUsers, setInitialUsers] = useState<User[]>([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const initialUsersRef = useRef<User[]>([]);
  const gridRefetchRef = useRef<() => void>();

  const { 
    form, 
    handleCustomGroupUsersSubmit, 
    isSaving,
    checkIfDirty
  } = useCustomGroupUsersForm(
    initialUsers,
    projectId,
    toolId,
    group?.groupName || '',
    toolProjectId
  );

  const {
    watch,
    reset: resetForm
  } = form;

  watch("users");
  const isDirty = checkIfDirty();
  const resetDialogState = () => {
    form.reset({ users: [] });
    setInitialUsers([]);
    initialUsersRef.current = [];
    setShowConfirmationModal(false);
    removeItem(sessionStorage, `CustomGroupUsers_${group?.groupName || ''}`);
  };

  const handleClose = () => {
    if (isDirty) {
      setShowConfirmationModal(true);
    } else {
      resetDialogState();
      onClose();
    }
  };

  const handleForceClose = () => {
    resetDialogState();
    onClose();
  };

  const handleSave = async () => {

    const success = await handleCustomGroupUsersSubmit();
   
    if (success) {
      setInitialUsers([]);
      initialUsersRef.current = [];
      gridRefetchRef.current?.();
    }
  };

  return (
    <>
      <NavigationPrompt
        when={isDirty}
        title="Do you want to leave this page?"
        description="You have unsaved changes that will be lost if you decide to continue."
      />
      <ActionConfirmation
        title="Unsaved Changes"
        description="You have unsaved changes. Are you sure you want to close without saving?"
        when={isDirty && showConfirmationModal}
        confirm={handleForceClose}
        cancel={() => setShowConfirmationModal(false)}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            height: '80vh',
            width: '90vw',
            maxWidth: '1200px'
          }
        }}
      >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="span">
            Edit Users for Custom Group: {group?.groupName}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ height: '100%', width: '100%' }}>
          <Box sx={{ height: 'calc(100% - 40px)', width: '100%' }}>
            <CustomGroupUsersTable
              form={form}
              fetchUsers={fetchUsers}
              projectId={projectId}
              toolId={toolId}
              groupName={group?.groupName || ''}
              initialUsersRef={initialUsersRef}
              setInitialUsers={setInitialUsers}
              refetchRef={gridRefetchRef}
            />
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={!isDirty || isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default EditCustomGroupUsersDialog;